/**
 * Compliance Management Workflow Landing Page
 *
 * WHY: This is the entry point for the Compliance Management workflow within ComplyIQ.
 * It provides clear calls-to-action for:
 * - Creating a new compliance assessment
 * - Opening an existing assessment
 * - Quick access to Controls, Assessments, and Evidence
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  FileCheck,
  Plus,
  FolderOpen,
  Target,
  ArrowRight,
  Shield,
  ClipboardCheck,
  FileCode,
  Search,
} from 'lucide-react'

export function ComplianceManagementPage() {
  const navigate = useNavigate()
  const [openMissionDialog, setOpenMissionDialog] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch missions
  const { data: missions = [], isLoading: missionsLoading } = useQuery({
    queryKey: ['platform', 'missions'],
    queryFn: () => api.platform.missions.list(),
  })

  const filteredMissions = missions.filter(m =>
    m.mission_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.mission_objective?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const activeMissions = missions.filter(m => m.status === 'active')

  const handleSelectMission = (missionId: string) => {
    setOpenMissionDialog(false)
    navigate(`/complyiq/compliance/controls?mission=${missionId}`)
  }

  const handleCreateMission = () => {
    navigate('/missions?action=create')
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="p-2">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <FileCheck className="h-6 w-6 text-emerald-400" />
            </div>
          </div>
          <h1 className="text-3xl font-display font-bold">Compliance Management</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Track regulatory controls, conduct assessments, and manage evidence
            for comprehensive compliance across multiple frameworks.
          </p>
        </div>
      </div>

      {/* Primary CTAs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create New Assessment */}
        <Card className="hover:border-primary/50 transition-colors group cursor-pointer"
              onClick={handleCreateMission}>
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <Plus className="h-8 w-8 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">New Compliance Assessment</h2>
                <p className="text-muted-foreground mb-4">
                  Start a new compliance assessment. Select frameworks, map controls,
                  and begin collecting evidence for your compliance program.
                </p>
                <Button variant="glow" className="group-hover:shadow-lg transition-shadow">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Open Existing */}
        <Card className="hover:border-primary/50 transition-colors group cursor-pointer"
              onClick={() => setOpenMissionDialog(true)}>
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-accent/10 group-hover:bg-accent/20 transition-colors">
                <FolderOpen className="h-8 w-8 text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Open Existing Assessment</h2>
                <p className="text-muted-foreground mb-4">
                  Continue working on an existing compliance assessment.
                  Review controls, update evidence, or generate reports.
                </p>
                <div className="flex items-center gap-3">
                  <Button variant="outline" className="group-hover:border-primary/50 transition-colors">
                    Browse Assessments
                    <FolderOpen className="ml-2 h-4 w-4" />
                  </Button>
                  {activeMissions.length > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {activeMissions.length} active
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Access Tools */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Workflow Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-500" />
                <CardTitle className="text-base">Controls</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mb-4">
                Manage compliance controls across frameworks (NIST, CMMC, FedRAMP)
              </CardDescription>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/complyiq/compliance/controls">
                  View Controls
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-base">Assessments</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mb-4">
                Conduct and track compliance assessments and evaluations
              </CardDescription>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/complyiq/compliance/assessments">
                  View Assessments
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileCode className="h-5 w-5 text-purple-500" />
                <CardTitle className="text-base">Evidence</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mb-4">
                Manage evidence artifacts and documentation
              </CardDescription>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/complyiq/compliance/evidence">
                  View Evidence
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Open Mission Dialog */}
      <Dialog open={openMissionDialog} onOpenChange={setOpenMissionDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Open Assessment
            </DialogTitle>
            <DialogDescription>
              Select an assessment to continue your compliance work
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search assessments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {missionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredMissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'No assessments match your search' : 'No assessments available'}
              </div>
            ) : (
              filteredMissions.map((mission) => (
                <button
                  key={mission.mission_id}
                  onClick={() => handleSelectMission(mission.mission_id)}
                  className="w-full p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-all text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="font-medium truncate">{mission.mission_name}</span>
                      </div>
                      {mission.mission_objective && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                          {mission.mission_objective}
                        </p>
                      )}
                    </div>
                    <Badge variant={mission.status === 'active' ? 'default' : 'secondary'} className="flex-shrink-0">
                      {mission.status}
                    </Badge>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="pt-4 border-t border-border">
            <Button onClick={handleCreateMission} variant="outline" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Create New Assessment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
